import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';

// layout 
import AuthLayout from '../../../layouts/AuthLayout/AuthLayout';

// components 
import { NioButton, NioToaster, NioCard, NioBrand, NioField, NioIcon } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

export default function Signup() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const [providerType, setProviderType] = useState('individual'); // 'individual' | 'company' | 'auto entrepeneur'
  const [companyDocs, setCompanyDocs] = useState([]);
  const [individualDocs, setIndividualDocs] = useState([]);
  const [autoEntrepreneurDocs, setAutoEntrepreneurDocs] = useState([]);
  const [cinPhotoFile, setCinPhotoFile] = useState(null);
  const [emailStatus, setEmailStatus] = useState('idle');

  // Tunisian regions (governorates) and their cities
  const tunisianRegions = {
    'Ariana': ['Ariana', 'Raoued', 'Kalâat el-Andalous', 'Sidi Thabet', 'Ettadhamen', 'Mnihla'],
    'Béja': ['Béja', 'Medjez el-Bab', 'Testour', 'Nefza', 'Amdoun', 'Téboursouk'],
    'Ben Arous': ['Ben Arous', 'Rades', 'Mégrine', 'Hammam Lif', 'Fouchana', 'Ezzahra', 'Mohamedia'],
    'Bizerte': ['Bizerte', 'Menzel Bourguiba', 'Mateur', 'Sejnane', 'Ghar El Melh', 'Ras Jebel'],
    'Gabès': ['Gabès', 'Médenine', 'Matmata', 'El Hamma', 'Ghannouch', 'Mareth'],
    'Gafsa': ['Gafsa', 'El Ksar', 'Métlaoui', 'Redeyef', 'Moularès', 'Sidi Aïch'],
    'Jendouba': ['Jendouba', 'Tabarka', 'Aïn Draham', 'Fernana', 'Bou Salem', 'Ghardimaou'],
    'Kairouan': ['Kairouan', 'Sbikha', 'Haffouz', 'Chebika', 'Oueslatia', 'Nasrallah'],
    'Kasserine': ['Kasserine', 'Sbeitla', 'Fériana', 'Thala', 'Haïdra', 'Jedelienne'],
    'Kébili': ['Kébili', 'Douz', 'Souk Lahad', 'Faouar', 'El Golâa'],
    'Kef': ['Le Kef', 'Tajerouine', 'Kalâat Khasba', 'Dahmani', 'Sers', 'Nebeur'],
    'Mahdia': ['Mahdia', 'Bou Merdes', 'Chebba', 'El Jem', 'Ksour Essef', 'Sidi Alouane'],
    'Manouba': ['Manouba', 'Douar Hicher', 'Oued Ellil', 'Mornag', 'Borj El Amri', 'Djedeida'],
    'Médenine': ['Médenine', 'Tataouine', 'Ben Gardane', 'Zarzis', 'Djerba', 'Houmt Souk'],
    'Monastir': ['Monastir', 'Moknine', 'Ksibet el-Médiouni', 'Bembla', 'Téboulba', 'Sayada'],
    'Nabeul': ['Nabeul', 'Hammamet', 'Kélibia', 'Korba', 'Menzel Temime', 'Dar Chaabane', 'Béni Khalled'],
    'Sfax': ['Sfax', 'Sakiet Ezzit', 'El Hencha', 'Agareb', 'Menzel Chaker', 'Bir Ali Ben Khalifa'],
    'Sidi Bouzid': ['Sidi Bouzid', 'Meknassy', 'Regueb', 'Ouled Haffouz', 'Cebbala Ouled Asker'],
    'Siliana': ['Siliana', 'Bou Arada', 'Gaâfour', 'El Krib', 'Makthar', 'Rouhia'],
    'Sousse': ['Sousse', 'Hammam Sousse', 'Akouda', 'Enfidha', 'Kalaa Kebira', 'Msaken', 'Bouficha'],
    'Tataouine': ['Tataouine', 'Remada', 'Bir Lahmar', 'Ghomrassen', 'Dehiba'],
    'Tozeur': ['Tozeur', 'Nefta', 'Degache', 'Hazoua', 'Tamaghza'],
    'Tunis': ['Tunis', 'La Marsa', 'Carthage', 'Sidi Bou Said', 'Le Bardo', 'La Goulette'],
    'Zaghouan': ['Zaghouan', 'Zriba', 'Nadhour', 'Fahs', 'Saouaf', 'El Fahs']
  };

  // Sort regions alphabetically
  const sortedRegions = Object.keys(tunisianRegions).sort();

  // Secteurs d'activité et sous-secteurs
  const secteursAvecSousSecteurs = {
    'Meubles & Décoration': ['Montage de meubles', 'Fixation murale (étagères, TV…)', 'Installation rideaux / tringles', 'Aménagement dressing / placard', 'Autres'],
    'Cuisine': ['Installation cuisine', 'Montage meubles cuisine', 'Pose plan de travail', 'Installation électroménager', 'Pose crédence', 'Autres'],
    'Salle de bain': ['Rénovation salle de bain', 'Installation douche / baignoire', 'Pose meuble vasque', 'Installation robinetterie', 'Petits travaux plomberie', 'Autres'],
    'Peinture': ['Peinture murale', 'Peinture plafond', 'Peinture plancher', 'Peinture portes', 'Peinture fenêtres', 'Autres'],
    'Menuiserie': ['Installation portes', 'Pose placards', 'Fabrication meubles sur mesure', 'Réparation bois', 'Pose étagères', 'Autres'],
    'Jardinage': ['Tonte pelouse', 'Taille haies', 'Plantation', 'Entretien jardin', 'Débroussaillage', 'Autres'],
    'Mécanique': ['Vidange moteur', 'Changement huile', 'Changement plaquettes de frein', 'Recharge clim auto', 'Autres'],
    'Déménagement': ['Aide déménagement', 'Transport meubles', 'Emballage cartons', 'Débarras', 'Montage / démontage meubles', 'Autres'],
    'Ménage & Nettoyage': ['Ménage à domicile', 'Nettoyage après travaux', 'Nettoyage vitres', 'Nettoyage fin de chantier', 'Entretien régulier', 'Autres'],
    'Sécurité & Serrurerie': ['Installation serrures', 'Dépannage serrurerie', 'Installation portes sécurisées', 'Réparation serrures', 'Autres'],
  };
  const secteursOptions = Object.keys(secteursAvecSousSecteurs).sort();

  const { register, handleSubmit, reset, formState: { errors }, watch, setValue, control, getValues } = useForm({
    'mode': 'onBlur',
    defaultValues: {
      civilite: '',
      nom: '',
      prenom: '',
      email: '',
      motDePasse: '',
      confirmMotDePasse: '',
      region: '',
      city: '',
      address: '',
      phone: '',
      role: '',
      // Champs société (prestataire)
      secteurActivite: [],
      /** Une entrée par ligne Service (alignée sur secteurActivite). */
      sousSecteurs: [''],
      anneeCreation: '',
      typeEntreprise: '',
      formeJuridique: '',
      numeroImmatriculation: '',
      numeroTVA: '',
      numCin: '',
      // Réseaux sociaux
      linkedin: '',
      facebook: '',
      twitter: '',
      instagram: '',
      website: '',
    }
  });

  const regionValue = watch('region');
  const secteurActiviteValue = watch('secteurActivite');
  const sousSecteursValue = watch('sousSecteurs');
  const emailValue = watch('email');
  const passwordValue = watch('motDePasse');

  const pwdChecks = {
    length: passwordValue && passwordValue.length >= 8,
    upper: passwordValue && /[A-Z]/.test(passwordValue),
    lower: passwordValue && /[a-z]/.test(passwordValue),
    digit: passwordValue && /[0-9]/.test(passwordValue),
  };

  useEffect(() => {
    if (!emailValue) {
      setEmailStatus('idle');
      return;
    }
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (!emailRegex.test(emailValue)) {
      setEmailStatus('idle');
      return;
    }
    setEmailStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const result = await api.checkEmail(emailValue);
        if (result && result.success) {
          setEmailStatus('taken');
        } else {
          setEmailStatus('available');
        }
      } catch {
        setEmailStatus('idle');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [emailValue]);

  const normalizeSelectedServices = (value) => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean);
    }
    if (value == null) return [];
    const v = String(value).trim();
    return v ? [v] : [];
  };
  const secteurValueArray = Array.isArray(secteurActiviteValue)
    ? secteurActiviteValue
    : (secteurActiviteValue ? [secteurActiviteValue] : []);
  const serviceRows = secteurValueArray.length
    ? secteurValueArray.map((v) => (v == null ? '' : String(v).trim()))
    : [''];

  const setServiceRows = (values) => {
    const normalized = values.map((v) => (v == null ? '' : String(v).trim()));
    setValue('secteurActivite', normalized, { shouldValidate: true });
    let subs = Array.isArray(getValues('sousSecteurs')) ? [...getValues('sousSecteurs')] : [];
    while (subs.length < normalized.length) subs.push('');
    while (subs.length > normalized.length) subs.pop();
    setValue('sousSecteurs', subs.length ? subs : [''], { shouldValidate: true });
  };

  const subSectorForRow = (rowIndex) => {
    const arr = Array.isArray(sousSecteursValue) ? sousSecteursValue : [];
    const v = arr[rowIndex];
    return v == null ? '' : String(v).trim();
  };

  const sousSecteursForSecteur = (secteurTitle) =>
    secteurTitle && secteursAvecSousSecteurs[secteurTitle] ? secteursAvecSousSecteurs[secteurTitle] : [];

  const secteursOptionsForRow = (rowIndex) =>
    secteursOptions.filter((opt) => {
      if (opt === serviceRows[rowIndex]) return true;
      return !serviceRows.some((picked, i) => i !== rowIndex && picked && picked === opt);
    });

  const updateServiceRow = (index, value) => {
    const normalized = value == null ? '' : String(value).trim();
    if (index > 0 && normalized === '') {
      const next = serviceRows.filter((_, i) => i !== index);
      setServiceRows(next.length ? next : ['']);
      return;
    }
    const next = [...serviceRows];
    while (next.length <= index) next.push('');
    next[index] = normalized;
    setServiceRows(next);
    let subs = Array.isArray(getValues('sousSecteurs')) ? [...getValues('sousSecteurs')] : [];
    while (subs.length < next.length) subs.push('');
    subs[index] = '';
    setValue('sousSecteurs', subs, { shouldValidate: true });
  };

  const addServiceRow = () => {
    if (serviceRows.length < secteursOptions.length) {
      setServiceRows([...serviceRows, '']);
    }
  };

  const removeServiceRow = (index) => {
    if (index === 0) return;
    const next = serviceRows.filter((_, i) => i !== index);
    setServiceRows(next.length ? next : ['']);
  };

  const setSubSectorRow = (rowIndex, raw) => {
    const value = raw == null ? '' : String(raw).trim();
    const subs = Array.isArray(getValues('sousSecteurs')) ? [...getValues('sousSecteurs')] : [''];
    while (subs.length <= rowIndex) subs.push('');
    subs[rowIndex] = value;
    setValue('sousSecteurs', subs, { shouldValidate: true, shouldDirty: true });
  };

  // Update cities when region changes
  useEffect(() => {
    if (regionValue && tunisianRegions[regionValue]) {
      const sortedCities = [...tunisianRegions[regionValue]].sort();
      setAvailableCities(sortedCities);
      setSelectedRegion(regionValue);
      // Reset city when region changes
      setValue('city', '');
    } else {
      setAvailableCities([]);
      setSelectedRegion('');
    }
  }, [regionValue, setValue]);

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onSubmit = async (data) => {
    
    setLoading(true);
    setShowError(false);
    setShowSuccess(false);
    setErrorMessage('');

    if (emailStatus === 'taken') {
      setErrorMessage(t('auth.emailTaken'));
      setShowError(true);
      setLoading(false);
      return;
    }

    // Validate password confirmation
    if (data.motDePasse !== data.confirmMotDePasse) {
      setErrorMessage(t('auth.errors.passwordMismatch'));
      setShowError(true);
      setLoading(false);
      return;
    }

    try {
      let verificationDocuments;
      if (data.role === 'provider') {
        const filesToSend = providerType === 'company'
          ? companyDocs
          : (providerType === 'auto entrepeneur' || providerType === 'individuelle'
            ? autoEntrepreneurDocs
            : individualDocs);
        if (filesToSend.length > 0) {
          const docs = [];
          for (const file of filesToSend) {
            try {
              docs.push(await readFileAsDataUrl(file));
            } catch (e) {
              console.warn('Doc read failed', e);
            }
          }
          verificationDocuments = docs.length ? docs : undefined;
        }
      }
      let cinPhotoData;
      if (cinPhotoFile && cinPhotoFile instanceof File) {
        try {
          cinPhotoData = await readFileAsDataUrl(cinPhotoFile);
        } catch (e) {
          console.warn('CIN photo read failed', e);
        }
      }

      const selectedServiceIds = normalizeSelectedServices(data.secteurActivite);
      const primaryServiceId = selectedServiceIds[0];
      if (data.role === 'provider' && selectedServiceIds.length === 0) {
        setErrorMessage(t('auth.errors.selectService'));
        setShowError(true);
        setLoading(false);
        return;
      }

      const rawSecteurs = Array.isArray(data.secteurActivite) ? data.secteurActivite : [];
      const rawSubs = Array.isArray(data.sousSecteurs) ? data.sousSecteurs : [];
      const serviceSubPairs = [];
      for (let i = 0; i < rawSecteurs.length; i += 1) {
        const sect = String(rawSecteurs[i] || '').trim();
        if (!sect) continue;
        const sub = String(rawSubs[i] || '').trim();
        const options = secteursAvecSousSecteurs[sect] || [];
        if (options.length > 0 && !sub) {
          setErrorMessage(
            t('auth.errors.subServiceRequired', { sect, line: i + 1 })
          );
          setShowError(true);
          setLoading(false);
          return;
        }
        serviceSubPairs.push({ sect, sub });
      }

      const combinedSubSector =
        serviceSubPairs.map((p) => p.sub).filter(Boolean).join(' | ') || undefined;
      const primarySubSector = serviceSubPairs[0]?.sub || undefined;

      const payload = {
        
        name: data.prenom,
        lastName: data.nom,
        civilite: data.civilite || undefined,
        email: data.email,
        password: data.motDePasse,
        phone: data.phone,
        role: data.role,
        region: data.region,
        city: data.city,
        address: data.address || '',
        ...(data.role === 'provider' && {
          userType: providerType === 'company' ? 'company' : 'individual',
        }),
        ...(data.role === 'provider' && providerType === 'company' && {
          serviceId: primaryServiceId || undefined,
          serviceIds: selectedServiceIds.length ? selectedServiceIds : undefined,
          serviceCategory: combinedSubSector || primarySubSector || undefined,
          sector: primaryServiceId || undefined,
          subSector: combinedSubSector || primarySubSector || undefined,
          creationYear: data.anneeCreation || undefined,
          registrationNumber: data.numeroImmatriculation || undefined,
          vatNumber: data.numeroTVA || undefined,
        }),
        ...(data.role === 'provider' && providerType !== 'company' && {
          // Pour auto-entrepreneur / individuel: stocker Service + Sous-service aussi.
          serviceId: primaryServiceId || undefined,
          serviceIds: selectedServiceIds.length ? selectedServiceIds : undefined,
          serviceCategory: combinedSubSector || primarySubSector || undefined,
          sector: primaryServiceId || undefined,
          subSector: combinedSubSector || primarySubSector || undefined,
        }),
        ...(data.role === 'provider' && providerType !== 'company' && data.numCin && { numCin: data.numCin }),
        ...(cinPhotoData && { cinPhoto: cinPhotoData }),
        ...(verificationDocuments && { verificationDocuments }),
        // Réseaux sociaux (optionnels)
        ...(data.linkedin && { linkedin: data.linkedin }),
        ...(data.facebook && { facebook: data.facebook }),
        ...(data.twitter && { twitter: data.twitter }),
        ...(data.instagram && { instagram: data.instagram }),
        ...(data.website && { website: data.website }),
      };

      const response = await api.register(payload);

      if (response && response.success) {
        const successText = data.role === 'provider'
          ? t('auth.errors.registrationSuccessProvider')
          : t('auth.errors.registrationSuccessClient');
        setSuccessMessage(successText);
        setShowSuccess(true);
        reset();
        navigate('/auth/login');
      } else {
        setErrorMessage(response?.message || t('auth.errors.registrationFailed'));
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Check if it's a connection error
      let errorMsg = error.message || t('auth.errors.registrationFailed');
      if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED'))) {
        errorMsg = t('auth.errors.serverConnectionError');
      }
      
      setErrorMessage(errorMsg);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Log all form errors for debugging
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).map(err => err?.message).filter(Boolean);
      if (errorMessages.length > 0) {
        setErrorMessage(errorMessages.join(', '));
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        }, 5000);
      }
    }
  }, [errors])


  return (
    <AuthLayout title={t('auth.signupTitle')} rootClass="layout-1">

      {
        showSuccess && (
          <NioToaster
            variant="success"
            messages={[{ id: 1, header: t('auth.signupTitle'), body: successMessage || t('auth.errors.registrationSuccessClient') }]}
          />
        )
      }
      {
        showError && (
          <NioToaster
            variant='error'
            messages={[{ id: 2, header: t('auth.signupTitle'), body: errorMessage }]}
          />
        )
      }


      <NioCard className="nk-form-card card card-gutter-md nk-auth-form-card mx-md-9 mx-xl-auto" >
        <NioCard.Body>
          <div className="nk-form-card-head text-center pb-5">
            <div className="form-logo mb-3">
              <NioBrand logo="logo1" imageRoot="../images/" />
            </div>
            <h3 className="title mb-2">{t('auth.signupHeading')}</h3>
            <p className="text">{t('auth.signupSub')} <Link to="/auth/login" className="btn-link text-primary">{t('auth.signinLink')}</Link>.</p>
          </div>
          <Form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Row className="gy-4">
            <Col xs={12}>
                <NioField htmlFor="civilite" label={t('auth.civility')}>
                  <NioField.Select id="civilite" errors={errors.civilite} {...register("civilite")}>
                    <option value="">{t('auth.selectCivility')}</option>
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                    <option value="Mlle">Mlle</option>
                  </NioField.Select>
                </NioField>
              </Col>
              <Col xs={12} md={6}>
                <NioField label={t('auth.lastName')} htmlFor="nom">
                  <NioField.Input id="nom" placeholder={t('auth.lastNamePlaceholder')} errors={errors.nom} {...register("nom", {
                    required: t('auth.errors.lastNameRequired')
                  })} />
                </NioField>
              </Col>
              <Col xs={12} md={6}>
                <NioField label={t('auth.firstName')} htmlFor="prenom">
                  <NioField.Input id="prenom" placeholder={t('auth.firstNamePlaceholder')} errors={errors.prenom} {...register("prenom", {
                    required: t('auth.errors.firstNameRequired')
                  })} />
                </NioField>
              </Col>
             
              <Col xs={12}>
                <NioField label={t('auth.email')} htmlFor="email">
                  <NioField.Input type="email" id="email" placeholder={t('auth.emailPlaceholder')} errors={errors.email} {...register("email", {
                    required: t('auth.errors.emailRequired'),
                    pattern: {
                      value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                      message: t('auth.errors.emailInvalid')
                    }
                  })} />
                  {emailStatus === 'checking' && (
                    <small className="form-text text-muted">{t('auth.emailChecking')}</small>
                  )}
                  {emailStatus === 'available' && (
                    <small className="form-text text-success">{t('auth.emailAvailable')}</small>
                  )}
                  {emailStatus === 'taken' && (
                    <small className="form-text text-danger">{t('auth.emailTaken')}</small>
                  )}
                </NioField>
              </Col>
                <Col xs={12}>
                <NioField htmlFor="phone" label={t('auth.phone')}>
                  <NioField.Input id="phone" type="tel" maxLength={8} placeholder={t('auth.phonePlaceholder')} errors={errors.phone} {...register("phone", {
                    required: t('auth.errors.phoneRequired'),
                    maxLength: {
                      value: 8,
                      message: t('auth.errors.phoneDigits')
                    },
                    pattern: {
                      value: /^[0-9]{8}$/,
                      message: t('auth.errors.phoneDigits')
                    }
                  })} />
                </NioField>
              </Col><br></br>
                <Col xs={12}>
                <NioField htmlFor="phone" label={t('auth.fixe')}>
                  <NioField.Input id="phone" type="tel" maxLength={8} placeholder={t('auth.fixePlaceholder')} errors={errors.phone} {...register("phone", {
                    required: t('auth.errors.fixeRequired'),
                    maxLength: {
                      value: 8,
                      message: t('auth.errors.fixeDigits')
                    },
                    pattern: {
                      value: /^[0-9]{8}$/,
                      message: t('auth.errors.fixeDigits')
                    }
                  })} />
                </NioField>
              </Col>
              <Col xs={12}>
                <NioField htmlFor="password" label={t('auth.password')}>
                  <NioField.Input
                    id="password"
                    type="password"
                    {...register("motDePasse", {
                      required: t('auth.errors.passwordRequired')
                    })}
                    icon="eye-fill after text-primary"
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                  {passwordValue && (
                    <div className="mt-2">
                      <small className={`d-block ${pwdChecks.length ? 'text-success' : 'text-muted'}`}>
                        {pwdChecks.length ? '\u2713' : '\u2022'} {t('auth.pwdLength')}
                      </small>
                      <small className={`d-block ${pwdChecks.upper ? 'text-success' : 'text-muted'}`}>
                        {pwdChecks.upper ? '\u2713' : '\u2022'} {t('auth.pwdUpper')}
                      </small>
                      <small className={`d-block ${pwdChecks.lower ? 'text-success' : 'text-muted'}`}>
                        {pwdChecks.lower ? '\u2713' : '\u2022'} {t('auth.pwdLower')}
                      </small>
                      <small className={`d-block ${pwdChecks.digit ? 'text-success' : 'text-muted'}`}>
                        {pwdChecks.digit ? '\u2713' : '\u2022'} {t('auth.pwdDigit')}
                      </small>
                    </div>
                  )}
                </NioField>
              </Col>
              <Col xs={12}>
                <NioField htmlFor="confirmPassword" label={t('auth.confirmPasswordLabel')}>
                  <NioField.Input
                    id="confirmPassword"
                    type="password"
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                        {...register("confirmMotDePasse", {
                      required: t('auth.errors.confirmPasswordRequired'),
                      validate: (value) => {
                        const password = getValues('motDePasse');
                        return value === password || t('auth.errors.passwordMismatch');
                      }
                    })}
                  />
                </NioField>
              </Col>
              <Col xs={12}>
                <NioField htmlFor="role" label={t('auth.role')}>
                  <NioField.Select id="role" errors={errors.role} {...register("role", {
                    required: t('auth.errors.roleRequired')
                  })}>
                    <option value="">{t('auth.selectRole')}</option>
                    <option value="user">{t('auth.user')}</option>
                    <option value="provider">{t('auth.provider')}</option>
                  </NioField.Select>
                </NioField>
              </Col>

              {watch('role') === 'provider' && (
                <>
                  <Col xs={12}>
                    <label className="form-label fw-semibold d-block mb-2">{t('auth.providerType')}</label>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className={`btn flex-fill ${providerType === 'company' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setProviderType('company')}
                      >
                        {t('auth.company')}
                      </button>
                      <button
                        type="button"
                        className={`btn flex-fill ${providerType === 'auto entrepeneur' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setProviderType('auto entrepeneur')}
                      >
                        {t('auth.autoEntrepreneur')}
                      </button>
                      <button
                        type="button"
                        className={`btn flex-fill ${providerType === 'individuelle' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setProviderType('individuelle')}
                      >
                        {t('auth.individual')}
                      </button>
                    </div>
                    <small className="text-muted d-block mt-2">
                      {t('auth.providerTypeHint')}
                    </small>
                  </Col>
                  <Controller
                    name="secteurActivite"
                    control={control}
                    rules={{
                      validate: (value) =>
                        normalizeSelectedServices(value).length > 0 ||
                        t('auth.errors.serviceRequired')
                    }}
                    render={({ field: { value, onChange, ref, name, onBlur } }) => (
                      <select
                        ref={ref}
                        name={name}
                        onBlur={onBlur}
                        multiple
                        className="d-none"
                        aria-hidden="true"
                        tabIndex={-1}
                        value={normalizeSelectedServices(value)}
                        onChange={(e) => {
                          const vals = Array.from(e.target.selectedOptions, (opt) => opt.value);
                          onChange(vals);
                        }}
                      >
                        {normalizeSelectedServices(value).map((id) => (
                          <option key={id} value={id}>
                            {id}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {(providerType === 'auto entrepeneur' || providerType === 'individuelle') && (
                    <>
                      <Col xs={12}>
                        <NioField htmlFor="secteurActivite-0" label={t('auth.service')}>
                          {serviceRows.map((currentValue, index) => {
                            const subOpts = sousSecteursForSecteur(currentValue);
                            const subVal = subSectorForRow(index);
                            return (
                              <div
                                key={`svc-block-ae-${index}-${currentValue}|${subVal}`}
                                className="p-3 mb-3 rounded border bg-light"
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="fw-semibold text-body small text-muted">
                                    {index === 0 ? t('auth.mainService') : t('auth.serviceN', { n: index + 1 })}
                                  </span>
                                  {index > 0 ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeServiceRow(index)}
                                    >
                                      {t('auth.remove')}
                                    </button>
                                  ) : null}
                                </div>
                                <NioField label={t('auth.service')} htmlFor={`secteurActivite-${index}`}>
                                  <NioField.Select
                                    key={`svc-sel-ae-${index}-${currentValue || 'empty'}`}
                                    id={`secteurActivite-${index}`}
                                    value={currentValue || ''}
                                    errors={index === 0 ? errors.secteurActivite : undefined}
                                    onChange={(e) => updateServiceRow(index, e.target.value)}
                                  >
                                    <option value="">{t('auth.selectService')}</option>
                                    {secteursOptionsForRow(index).map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </NioField.Select>
                                </NioField>
                                {currentValue && subOpts.length > 0 && (
                                  <div className="mt-3">
                                    <NioField label={t('auth.subService')} htmlFor={`sousSecteurs-ae-${index}`}>
                                      <NioField.Select
                                        key={`sub-sel-ae-${index}-${currentValue}`}
                                        id={`sousSecteurs-ae-${index}`}
                                        value={subVal}
                                        onChange={(e) => setSubSectorRow(index, e.target.value)}
                                      >
                                        <option value="">{t('auth.selectSubService')}</option>
                                        {subOpts.map((ss) => (
                                          <option key={ss} value={ss}>
                                            {ss}
                                          </option>
                                        ))}
                                      </NioField.Select>
                                    </NioField>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div className="d-flex justify-content-end gap-1 mt-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm text-uppercase fw-semibold d-flex align-items-center"
                              onClick={addServiceRow}
                              disabled={serviceRows.length >= secteursOptions.length}
                            >
                              <NioIcon size="sm" name="plus" className="me-2" />
                              {t('auth.add')}
                            </button>
                          </div>
                          <small className="text-muted d-block mt-2">
                            {t('auth.service')}/{t('auth.subService')} — {t('auth.add')}
                          </small>
                        </NioField>
                      </Col>
                      <Col xs={12}>
                        <NioField htmlFor="numCin" label={t('auth.numCIN')}>
                          <NioField.Input
                            id="numCin"
                            placeholder={t('auth.cinPlaceholder')}
                            errors={errors.numCin}
                            {...register('numCin')}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12}>
                        <NioField htmlFor="cinPhoto" label={t('auth.cinPhoto')}>
                          <input
                            id="cinPhoto"
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              setCinPhotoFile(f || null);
                            }}
                          />
                          <small className="text-muted d-block mt-1">
                            {t('auth.cinHint')}
                          </small>
                        </NioField>
                      </Col>
                      <Col xs={12}>
                      <NioField htmlFor="auto entrepeneurDocs" label={t('auth.docsAuto')}>
                        <input
                          id="auto entrepeneurDocs"
                          type="file"
                          accept=".pdf,image/*"
                          multiple
                          className="form-control"
                          onChange={(e) => {
                            const files = e.target.files ? Array.from(e.target.files) : [];
                            setAutoEntrepreneurDocs(files);
                          }}
                        />
                        {autoEntrepreneurDocs.length > 0 && (
                          <small className="text-muted d-block mt-2">
                            {t('auth.filesSelected', { count: autoEntrepreneurDocs.length, names: autoEntrepreneurDocs.map(f => f.name).join(', ') })}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          {t('auth.docsHint')}
                        </small>
                      </NioField>
                    </Col>
                    </>
                  )}
                  {providerType === 'company' && (
                    <>
                      <Col xs={12}>
                        <NioField htmlFor="secteurActivite-co-0" label={t('auth.sector')}>
                          {serviceRows.map((currentValue, index) => {
                            const subOpts = sousSecteursForSecteur(currentValue);
                            const subVal = subSectorForRow(index);
                            return (
                              <div
                                key={`svc-block-co-${index}-${currentValue}|${subVal}`}
                                className="p-3 mb-3 rounded border bg-light"
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="fw-semibold text-body small text-muted">
                                    {index === 0 ? t('auth.mainSector') : t('auth.sectorN', { n: index + 1 })}
                                  </span>
                                  {index > 0 ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeServiceRow(index)}
                                    >
                                      {t('auth.remove')}
                                    </button>
                                  ) : null}
                                </div>
                                <NioField label={t('auth.sector')} htmlFor={`company-secteurActivite-${index}`}>
                                  <NioField.Select
                                    key={`svc-sel-co-${index}-${currentValue || 'empty'}`}
                                    id={`company-secteurActivite-${index}`}
                                    value={currentValue || ''}
                                    errors={index === 0 ? errors.secteurActivite : undefined}
                                    onChange={(e) => updateServiceRow(index, e.target.value)}
                                  >
                                    <option value="">{t('auth.selectSector')}</option>
                                    {secteursOptionsForRow(index).map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </NioField.Select>
                                </NioField>
                                {currentValue && subOpts.length > 0 && (
                                  <div className="mt-3">
                                    <NioField label={t('auth.subSector')} htmlFor={`sousSecteurs-co-${index}`}>
                                      <NioField.Select
                                        key={`sub-sel-co-${index}-${currentValue}`}
                                        id={`sousSecteurs-co-${index}`}
                                        value={subVal}
                                        onChange={(e) => setSubSectorRow(index, e.target.value)}
                                      >
                                        <option value="">{t('auth.selectSubSector')}</option>
                                        {subOpts.map((ss) => (
                                          <option key={ss} value={ss}>
                                            {ss}
                                          </option>
                                        ))}
                                      </NioField.Select>
                                    </NioField>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div className="d-flex justify-content-end gap-1 mt-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm text-uppercase fw-semibold d-flex align-items-center"
                              onClick={addServiceRow}
                              disabled={serviceRows.length >= secteursOptions.length}
                            >
                              <NioIcon size="sm" name="plus" className="me-2" />
                              {t('auth.add')}
                            </button>
                          </div>
                          <small className="text-muted d-block mt-2">
                            {t('auth.sector')}/{t('auth.subSector')} — {t('auth.add')}
                          </small>
                        </NioField>
                      </Col>
                      <Col xs={12} md={6}>
                        <NioField htmlFor="anneeCreation" label={t('auth.creationYear')}>
                          <NioField.Input
                            id="anneeCreation"
                            type="number"
                            min="1900"
                            max={new Date().getFullYear()}
                            placeholder={t('auth.creationYearPlaceholder')}
                            errors={errors.anneeCreation}
                            {...register('anneeCreation')}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12} md={6}>
                        <NioField htmlFor="numeroImmatriculation" label={t('auth.siret')}>
                          <NioField.Input
                            id="numeroImmatriculation"
                            placeholder={t('auth.siretPlaceholder')}
                            errors={errors.numeroImmatriculation}
                            {...register('numeroImmatriculation')}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12} md={6}>
                        <NioField htmlFor="numeroTVA" label={t('auth.vatNumber')}>
                          <NioField.Input
                            id="numeroTVA"
                            placeholder={t('auth.vatPlaceholder')}
                            errors={errors.numeroTVA}
                            {...register('numeroTVA')}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12}>
                        <NioField htmlFor="companyDocs" label={t('auth.docsCompany')}>
                          <input
                            id="companyDocs"
                            type="file"
                            accept=".pdf,image/*"
                            multiple
                            className="form-control"
                            onChange={(e) => {
                              const files = e.target.files ? Array.from(e.target.files) : [];
                              setCompanyDocs(files);
                            }}
                          />
                          {companyDocs.length > 0 && (
                            <small className="text-muted d-block mt-2">
                              {t('auth.filesSelected', { count: companyDocs.length, names: companyDocs.map(f => f.name).join(', ') })}
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            {t('auth.docsCompanyHint')}
                          </small>
                        </NioField>
                      </Col>
                    </>
                  )}
                </>
              )}

          
          
              <Col xs={12}>
                <NioField htmlFor="region" label={t('auth.region')}>
                  <NioField.Select
                    id="region"
                    errors={errors.region}
                    {...register("region", {
                      required: t('auth.errors.regionRequired')
                    })}
                    onChange={(e) => {
                      const val = e.target.value;
                      setValue('region', val);
                      setSelectedRegion(val);
                      setValue('city', '');
                    }}
                  >
                    <option value="">{t('auth.selectRegion')}</option>
                    {sortedRegions.map((region, index) => (
                      <option key={`region-${index}`} value={region}>
                        {region}
                      </option>
                    ))}
                  </NioField.Select>
                </NioField>
              </Col>
              <Col xs={12}>
                <NioField htmlFor="city" label={t('auth.city')}>
                  <NioField.Select
                    id="city"
                    errors={errors.city}
                    {...register("city", {
                      required: t('auth.errors.cityRequired')
                    })}
                    disabled={!regionValue || availableCities.length === 0}
                  >
                    <option value="">{t('auth.selectCity')}</option>
                    {availableCities.map((city, index) => (
                      <option key={`city-${index}`} value={city}>
                        {city}
                      </option>
                    ))}
                  </NioField.Select>
                </NioField>
              </Col>
              <Col xs={12}>
                <NioField htmlFor="address" label={t('auth.address')}>
                  <NioField.Input
                    id="address"
                    placeholder={t('auth.addressPlaceholder')}
                    errors={errors.address}
                    {...register("address", {
                      required: t('auth.errors.addressRequired')
                    })}
                  />
                </NioField>
              </Col>
              <Col xs={12}>
                <div className="form-group">
                  <NioButton
                    label={loading ? t('auth.signupSubmitting') : t('auth.signupButtonFull')}
                    className="btn-block btn-primary"
                    type="submit"
                    disabled={loading}
                    onClick={(e) => {
                      // Don't prevent default here, let the form handle it
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        </NioCard.Body>
      </NioCard>
    </AuthLayout >
  )
}
